import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { IconMore } from '@zillow/constellation';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import Constants from 'app/shared/constants';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import PostingActions from 'app/shared/actions/PostingActions';
import Tour from 'app/shared/model/Tour';
import {
    getModelId,
    getTourMode,
    updateTour,
} from 'app/client/utils/3dToursUtils';

const { track } = analyticsUtils;
const { GA_EVENTS, TOUR_LOCATION, TOUR_TYPE } = AnalyticsConstants;
const { SUCCESS, ERROR } = NotificationConstants;
const { showModal, hideModal } = ModalActions;
const { VIEW_TOUR_MODAL, SELECT_TOUR_MODAL } = ModalConstants;
const {
    property: {
        vrModel: {
            getAll,
            associateListing,
            associateModel,
            publishListing,
            publishModel,
        },
    },
} = PostingActions;
const TOUR_HEADER_TEXT = {
    floorplan: 'Floor plan',
    unit: 'Unit',
    amenity: 'Amenity',
    singleFamily: 'Property ',
};

import {
    StyledTourCardEditableControls,
    StyledTourCardEditableControlsContainer,
    StyledTourCardEditableControlsDropdown,
    StyledTourCardEditableControlsDropdownElement,
} from './StyledComponents';

const TourCardEditableControls = ({
    floorPlanIndex = null,
    formData,
    showPublish,
    size,
    tour,
    tourType,
    unitIndex = null,
    updateFormData,
    updateStatusMessage,
}) => {
    const dispatch = useDispatch();
    const { aliasEncoded, isCommunity, syndication: { zillow } } = formData;

    const handleAssociate = ({
        shouldAssociate = false,
        tourId,
    }) => {
        if (floorPlanIndex !== null || unitIndex !== null) {
            const modelId = getModelId({
                floorPlanIndex,
                formData,
                unitIndex,
            });

            return dispatch(associateModel(tourId, aliasEncoded, modelId, shouldAssociate));
        }
        return dispatch(associateListing(tourId, aliasEncoded, shouldAssociate));
    };

    const handleAddTour = () => {
        const mode = getTourMode({
            isCommunity,
            floorPlanIndex,
            unitIndex,
        });
        const existingTourId = tour.id;

        const zillowDisplayStatus = zillow && zillow.displayStatus && zillow.displayStatus.toLowerCase();
        const isListingPublished = Constants.LISTING_STATUS.POSTED.toLowerCase() === zillowDisplayStatus;
        const gaEvent = existingTourId === '' ? GA_EVENTS.ADD_TOUR : GA_EVENTS.EDIT_TOUR;

        dispatch(track.GAEventV2(gaEvent, { label: TOUR_LOCATION.CARD + TOUR_TYPE[mode] }));
        dispatch(showModal({
            header: `${TOUR_HEADER_TEXT[mode]} tour`,
            modalType: SELECT_TOUR_MODAL,
            initialSelectedTourId: existingTourId || null,
            filterOutTourIds: formData.tours.map(tour => tour.id),
            mode,
            isListingPublished,
            onCancel: () => dispatch(hideModal()),
            onSubmit: (selectedTourId = null) => {
                dispatch(track.GAEventV2(GA_EVENTS.SUBMIT_TOUR, {
                    label: TOUR_LOCATION.MODAL + TOUR_TYPE[mode],
                }));
                if (selectedTourId === null && existingTourId !== '') {
                    updateStatusMessage('Removing...');
                    return handleAssociate({
                        shouldAssociate: false,
                        tourId: existingTourId,
                    }).then(() => {
                        updateStatusMessage('3d tour removed!', SUCCESS);
                        updateTour({
                            floorPlanIndex,
                            formData,
                            tourToUpdate: new Tour(),
                            tourType,
                            previousVrModelId: existingTourId,
                            unitIndex,
                            updateFormData,
                        });
                    })
                        .catch(() => updateStatusMessage('Cannot remove this 3d tour', ERROR))
                        .finally(() => dispatch(hideModal()));
                }

                if (selectedTourId && selectedTourId !== existingTourId) {
                    return (existingTourId !== ''
                        ? handleAssociate({ shouldAssociate: false, tourId: existingTourId })
                        : Promise.resolve())
                        .then(() => handleAssociate({
                            shouldAssociate: true,
                            tourId: selectedTourId,
                        })).then((newTour) => {
                            updateTour({
                                floorPlanIndex,
                                formData,
                                tourToUpdate: newTour,
                                tourType,
                                previousVrModelId: existingTourId,
                                unitIndex,
                                updateFormData,
                            });
                            dispatch(hideModal());
                        }).catch(() => Promise.reject());
                }

                dispatch(hideModal());
                return Promise.resolve();
            },
            onLoadTours: () => dispatch(getAll(aliasEncoded)),
        }));
    };

    const handleRemoveTour = (e) => {
        e.preventDefault();
        const mode = getTourMode({
            isCommunity,
            floorPlanIndex,
            unitIndex,
        });
        dispatch(track.GAEventV2(GA_EVENTS.REMOVE_TOUR, {
            label: TOUR_LOCATION.CARD + TOUR_TYPE[mode],
        }));
        updateStatusMessage('Removing...');
        handleAssociate({
            shouldAssociate: false,
            tourId: tour.id,
        }).then(() => {
            updateStatusMessage('3d tour removed!', SUCCESS);
            updateTour({
                floorPlanIndex,
                formData,
                tourToUpdate: new Tour(),
                tourType,
                previousVrModelId: tour.id,
                unitIndex,
                updateFormData,
            });
        }).catch(() => updateStatusMessage('Cannot remove this 3d tour', ERROR));
    };

    const sendPublishAction = (tourId) => {
        const shouldPublish = tour.state !== 'active';
        if (floorPlanIndex !== null || unitIndex !== null) {
            const modelId = getModelId({
                formData,
                floorPlanIndex,
                unitIndex,
            });
            return dispatch(publishModel(aliasEncoded, tourId, modelId, shouldPublish));
        }
        return dispatch(publishListing(tourId, aliasEncoded, shouldPublish));
    };

    const handlePublish = (e) => {
        e.preventDefault();
        const shouldPublish = tour.state !== 'active';
        const publishText = shouldPublish ? 'publish' : 'unpublish';
        const mode = getTourMode({
            isCommunity,
            floorPlanIndex,
            unitIndex,
        });

        const gaEvent = shouldPublish
            ? GA_EVENTS.PUBLISH_TOUR
            : GA_EVENTS.UNPUBLISH_TOUR;

        updateStatusMessage(shouldPublish ? 'Publishing...' : 'Unpublishing...');
        dispatch(track.GAEventV2(gaEvent, {
            label: TOUR_LOCATION.CARD + TOUR_TYPE[mode],
        }));
        sendPublishAction(tour.id)
            .then((publishedTour) => {
                updateStatusMessage(`3d tour ${publishText}ed!`, SUCCESS);
                updateTour({
                    floorPlanIndex,
                    formData,
                    tourToUpdate: publishedTour,
                    tourType,
                    previousVrModelId: tour.id,
                    unitIndex,
                    updateFormData,
                });
            })
            .catch(() => updateStatusMessage(`Cannot ${publishText} this 3D tour`, ERROR));
    };

    const handleViewTour = () => {
        const mode = getTourMode({
            isCommunity,
            floorPlanIndex,
            unitIndex,
        });
        dispatch(track.GAEventV2(GA_EVENTS.VIEW_TOUR, {
            label: TOUR_LOCATION.CARD + TOUR_TYPE[mode],
        }));

        dispatch(showModal({
            header: tour.name,
            onClose: () => dispatch(hideModal()),
            src: tour.src,
            modalType: VIEW_TOUR_MODAL,
            title: tour.name,
            isFluid: true,
        }));
    };

    return (
        <StyledTourCardEditableControls>
            <StyledTourCardEditableControlsContainer
                size={size}
            >
                <IconMore size="xs" fontColor="brand" />
                <StyledTourCardEditableControlsDropdown>
                    <StyledTourCardEditableControlsDropdownElement onClick={handleViewTour}>
                        View
                    </StyledTourCardEditableControlsDropdownElement>
                    <StyledTourCardEditableControlsDropdownElement onClick={handleAddTour}>
                        Edit
                    </StyledTourCardEditableControlsDropdownElement>
                    {showPublish && (
                        <StyledTourCardEditableControlsDropdownElement onClick={handlePublish}>
                            {tour.state === 'active' ? 'Unpublish' : 'Publish'}
                        </StyledTourCardEditableControlsDropdownElement>
                    )}
                    <StyledTourCardEditableControlsDropdownElement onClick={handleRemoveTour}>
                        Remove
                    </StyledTourCardEditableControlsDropdownElement>
                </StyledTourCardEditableControlsDropdown>
            </StyledTourCardEditableControlsContainer>
        </StyledTourCardEditableControls>
    );
};

TourCardEditableControls.propTypes = {
    tour: PropTypes.shape({
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    }).isRequired,
    tourType: PropTypes.oneOf(['amenity', 'floorPlan', 'unit', 'singleFamily']).isRequired,
    size: PropTypes.oneOf(['sm', 'md']),
    showPublish: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    updateFormData: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

TourCardEditableControls.defaultProps = {
    size: 'sm',
    showPublish: true,
};

export default TourCardEditableControls;
